<script setup>
import { breakpointsTailwind } from '@vueuse/core';
/**
 * Useful to display an image from a media model in a component.
 */
import { computed } from 'vue';

const props = defineProps({
    // Media model
    media: Object,
    // Set the sizes attribute on the img tag
    sizes: {
        type: [String, Object],
    },
    // Sets the image to a 0x0 transparent svg if the screen is wider than this, preventing the image from downloading
    // If a tailwind breakpoint is passed, the hide and show classes are added.
    maxWidth: String,
    // Sets the image to a 0x0 transparent svg if the screen is narrower than this, preventing the image from downloading.
    // If a tailwind breakpoint is passed, the hide and show classes are added.
    minWidth: String,
    // Set the class attribute on the img tag
    classes: String,
    // Set the title attribute on the img tag overwriting the media model title
    title: String,
    // Set the alt attribute on the img tag overwriting the media model alt
    alt: String,
    // Set the loading attribute on the img tag, defaults to lazy
    eager: Boolean,

    focalPoint: String,
});

const alt = computed(() => props.alt || props.media.alt);

const sizes = computed(() => {
    if (typeof props.sizes === 'string') {
        return props.sizes;
    }

    if (typeof props.sizes === 'object') {
        return Object.entries(props.sizes)
            .map(([key, value]) => (key === 'default' ? value : `(min-width: ${key}) ${value}`))
            .join(', ');
    }

    return null;
});

if (props.maxWidth && !props.maxWidth.endsWith('px')) {
    const classes = `${props.classes} ${props.maxWidth}:hidden block`;
}

if (props.minWidth && !props.minWidth.endsWith('px')) {
    const classes = `${props.classes} ${props.minWidth}:block hidden`;
}

function getWithInPixels(width) {
    return width.endsWith('px') ? width : `${breakpointsTailwind[width]}px`;
}

const minWithInPixels = computed(() => getWithInPixels(props.minWidth).replace('px', '') - 1 + 'px');

const title = computed(() => props.title || props.media.title);

const placeholder =
    'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIwIiBoZWlnaHQ9IjAiIHZpZXdCb3g9IjAgMCAwIDAiPjwvc3ZnPgo=';
</script>

<template>
    <picture v-if="media">
        <source
            v-if="minWidth"
            :srcset="placeholder"
            :media="`(max-width: ${minWithInPixels})`"
            class="hidden"
        >
        <source
            v-if="maxWidth"
            :srcset="placeholder"
            :media="`(min-width: ${getWithInPixels(maxWidth)})`"
            class="hidden"
        >

        <img
            :loading="eager ? 'eager' : 'lazy'"
            :src="media.src"
            :srcSet="media.src_set"
            :sizes="sizes"
            :alt="alt"
            :class="classes"
            :title="title"
            :style="focalPoint ? `object-position: ${focalPoint};` : ''"
            draggable="false"
        >
    </picture>
</template>
